import React from 'react';
import { Link, useLocation } from 'react-router-dom';

import {
    Home,
    User,
    ShoppingCart,
    Clock,
    Fish,
    ShoppingBag,
    ChevronUp,
    ChevronDown,
    Circle,
    Eye,
    FolderOpen,
    BarChart3,
    X,
    TrendingUp,
    UserLock,
    Truck
} from 'lucide-react';

import useAppContext from 'context/AppContext';
import { ROLE_PARENT_ACCESS, ROLE_SUBMENU_ACCESS } from 'constants/permissions';
import { FaCashRegister } from 'react-icons/fa';
import { TbCash } from 'react-icons/tb';

interface SidebarProps {
    isOpen: boolean;
    toggleSidebar: () => void;
}

const Sidebar = ({ isOpen, toggleSidebar }: SidebarProps) => {
    const location = useLocation();

    const { userDetail } = useAppContext();
    const userRole = userDetail?.role;

    const menuItems = [
        { key: "Dashboard", to: "/", label: "Dashboard", icon: Home },
        { key: "Absensi", to: "/attendance", label: "Absensi", icon: UserLock },
        { key: "Customer CRM", to: "/users", label: "Customer CRM", icon: User },
        { key: "Staff Accounts", to: "/staff", label: "Staff Accounts", icon: UserLock },
        {
            key: "Manajemen",
            label: "Manajemen",
            icon: FolderOpen,
            children: [
                { to: "/banners", label: "Banner" },
                { to: "/satuan", label: "Satuan" },
                { to: "/categories", label: "Kategori" },
                { to: "/vouchers", label: "Voucher" },
                { to: "/discounts", label: "Diskon" },
                { to: "/purchase/suppliers", label: "Supplier" },
                { to: "/affiliators", label: "Affiliator" },
                { to: "/products/dynamic-pricing", label: "Harga Dinamis" },
            ],
        },

        {
            key: "Pembayaran",
            label: "Pembayaran",
            icon: FaCashRegister,
            children: [
                { to: "/cashier", label: "Kasir" },
                { to: "/cashier/report", label: "Laporan" },
            ],
        },

        {
            key: "Keuangan",
            label: "Keuangan",
            icon: TbCash,
            children: [
                { to: "/cashflow/expense", label: "Biaya Pengeluaran" },
                { to: "/cashflow/depreciation", label: "Susut Barang" },
                { to: "/cashflow/business-to-business", label: "B2B" },
                { to: "/cashflow/business-to-customer", label: "B2C" },
                { to: "/cashflow/recap-revenue-customer", label: "Rekap B2C" },
                { to: "/cashflow/monthly-report", label: "Arus Kas" },
                { to: "/cashflow/profit-loss", label: "Laba Rugi" },
            ],
        },

        {
            key: "Pembelian",
            label: "Pembelian",
            icon: ShoppingCart,
            children: [
                { to: "/purchase", label: "Data Pembelian" },
                { to: "/purchase/item", label: "Data Aliran Barang" }
            ],
        },

        {
            key: "Penjualan",
            label: "Penjualan",
            icon: TrendingUp,
            children: [
                { to: "/orders", label: "Data Penjualan" },
                { to: "/selling/create", label: "Tambah Penjualan" },
            ],
        },

        { key: "Products", to: "/products", label: "Products", icon: Fish },

        { key: "Packing Order", to: "/orders/packing", label: "Packing Order", icon: ShoppingBag },

        { key: "Antrian Order", to: "/orders/queue", label: "Antrian Order", icon: Clock },

        { key: "Pengiriman Kurir", to: "/orders/courier", label: "Pengiriman Kurir", icon: Truck },

        { key: "Report", to: "/reports", label: "Report", icon: BarChart3 },
    ];

    const allowedParents = ROLE_PARENT_ACCESS[userRole] || [];

    const filteredMenu =
        allowedParents.includes("ALL")
            ? menuItems
            : menuItems.filter(m => allowedParents.includes(m.key));

    const [openSubmenu, setOpenSubmenu] = React.useState<string | null>(null);

    const toggleSubmenu = (key: string) => {
        setOpenSubmenu(prev => (prev === key ? null : key));
    };

    const canAccessSubmenu = (role: string, parentKey: string, submenuLabel: string) => {
        const access = ROLE_SUBMENU_ACCESS[role];

        if (access === "ALL") return true;
        if (!access || !access[parentKey]) return true;

        return access[parentKey].includes(submenuLabel);
    };

    const linkClasses = (path: string) =>
        `flex items-center text-green-900 p-2 rounded transition-all duration-300 
        ${location.pathname === path ? 'bg-green-100 font-semibold' : 'hover:bg-green-100'}`;

    const iconClasses = isOpen ? "mr-3" : "mx-auto";

    React.useEffect(() => {
        if (!isOpen) setOpenSubmenu(null);
    }, [isOpen]);

    return (
        <div
            className={`
                bg-white h-screen shadow-md flex flex-col
                transition-all duration-300 ease-in-out
                fixed inset-y-0 left-0 z-50
                md:relative md:z-0
                ${isOpen 
                    ? 'w-72 md:w-64 translate-x-0 shadow-2xl md:shadow-md' 
                    : '-translate-x-full md:translate-x-0 md:w-20 w-72'
                }
            `}
        >
            {/* Logo - Fixed at top */}
            <div className="p-4 flex-shrink-0 border-b border-gray-100 md:border-b-0">
                <div className={`flex items-center justify-between text-xl font-semibold text-green-900 ${isOpen ? 'mb-2 md:mb-6' : 'mb-4'}`}>
                    {isOpen ? (
                        <div className="flex items-center space-x-2">
                            <img src="/sayursembalun-logo.png" alt="Logo" className="w-8 h-8 object-contain" />
                            <span className="truncate">Sayur Sembalun</span>
                        </div>
                    ) : (
                        <img src="/sayursembalun-logo.png" alt="Logo" className="w-10 h-10 object-contain mx-auto" />
                    )}

                    {/* Tombol Close khusus Mobile */}
                    <button
                        type="button"
                        onClick={toggleSidebar}
                        className="p-1.5 rounded-lg text-gray-500 hover:text-gray-800 hover:bg-gray-100 md:hidden active:scale-95"
                        aria-label="Tutup Menu"
                    >
                        <X size={20} />
                    </button>
                </div>
            </div>

            {/* Navigation - Scrollable */}
            <div className="flex-1 overflow-y-auto px-4 scrollbar-thin scrollbar-thumb-green-200 scrollbar-track-transparent hover:scrollbar-thumb-green-300">
                <nav className="flex flex-col space-y-2 pb-4">
                    {filteredMenu.map((item: any) => {
                        const Icon = item.icon;

                        // No submenu
                        if (!item.children) {
                            return (
                                <Link 
                                    key={item.to} 
                                    to={item.to!} 
                                    className={linkClasses(item.to!)}
                                    onClick={() => {
                                        if (window.innerWidth < 768) {
                                            toggleSidebar();
                                        }
                                    }}
                                >
                                    <Icon className={iconClasses} size={20} />
                                    {isOpen && <span className="text-sm">{item.label}</span>}
                                </Link>
                            );
                        }

                        // With submenu
                        return (
                            <div key={item.key}>
                                <button
                                    onClick={() => toggleSubmenu(item.key)}
                                    className={`
                                        w-full flex items-center p-2 rounded text-green-900
                                        hover:bg-green-100 transition-all duration-300
                                        ${openSubmenu === item.key ? "bg-green-100 font-semibold" : ""}
                                    `}
                                >
                                    <Icon className={iconClasses} size={20} />
                                    {isOpen && (
                                        <div className="flex justify-between w-full">
                                            <span className="text-sm">{item.label}</span>
                                            {openSubmenu === item.key ? (
                                                <ChevronUp size={16} />
                                            ) : (
                                                <ChevronDown size={16} />
                                            )}
                                        </div>
                                    )}
                                </button>

                                {isOpen && openSubmenu === item.key && (
                                    <div className="flex flex-col space-y-1 mt-1">
                                        {item.children
                                            .filter((sub: any) => canAccessSubmenu(userRole, item.key, sub.label))
                                            .map((sub: any) => (
                                                <Link
                                                    key={sub.to}
                                                    to={sub.to}
                                                    className={`
                                                        flex items-center text-green-800 p-2 rounded
                                                        hover:bg-green-100 transition
                                                        ${location.pathname === sub.to ? "bg-green-100 font-medium" : ""}
                                                    `}
                                                    onClick={() => {
                                                        if (window.innerWidth < 768) {
                                                            toggleSidebar();
                                                        }
                                                    }}
                                                >
                                                    <Circle className="mr-3" size={6} fill="currentColor" />
                                                    <span className="text-sm">{sub.label}</span>
                                                </Link>
                                            ))}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </nav>
            </div>

            {/* Bottom section - Fixed at bottom */}
            <div className="flex flex-col items-center p-4 flex-shrink-0 border-t border-gray-200">
                {isOpen && (
                    <div className="text-gray-500 text-sm mb-2">
                        {process.env.REACT_APP_VERSION}
                    </div>
                )}

                <button
                    onClick={toggleSidebar}
                    className="flex items-center justify-center w-10 h-10 
                    bg-green-100 text-green-900 rounded-full
                    hover:bg-green-200 transition-all duration-300"
                    title={isOpen ? "Tutup Menu" : "Buka Menu"}
                >
                    {isOpen ? <X size={20} /> : <Eye size={20} />}
                </button>
            </div>
        </div>
    );
};

export default Sidebar;